import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  Users,
  DollarSign,
  Calendar,
  Target,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Edit,
  Plus,
  Filter,
  Download,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import './ProjectDashboard.css';

const ProjectDashboard = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  // State management
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    // KPI Cards
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalBudget: 0,
    totalSpent: 0,
    averageProgress: 0,

    // Project Status Distribution
    statusDistribution: {
      planning: 0,
      active: 0,
      onHold: 0,
      completed: 0,
      cancelled: 0,
    },

    // Priority Distribution
    priorityDistribution: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    },

    // Budget vs Actual
    budgetData: [],

    // Timeline Data
    timelineData: [],

    // Team Performance
    teamPerformance: [],

    // Recent Activities
    recentActivities: [],

    // Upcoming Milestones
    upcomingMilestones: [],

    // Risk Analysis
    riskAnalysis: {
      totalRisks: 0,
      highRisks: 0,
      mediumRisks: 0,
      lowRisks: 0,
    },

    // Project Health
    projectHealth: {
      healthy: 0,
      atRisk: 0,
      critical: 0,
    },
  });

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const [showBudgetModal, setShowBudgetModal] = useState(false);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const apiUrl = `${API_CONFIG.ENDPOINTS.PROJECTS}/dashboard`;
      console.log('Loading dashboard data from:', apiUrl);

      // Load dashboard data from the backend endpoint
      const dashboardResponse = await fetch(apiUrl, {
        headers: API_CONFIG.HEADERS,
      });

      console.log('Dashboard response status:', dashboardResponse.status);

      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        console.log('Dashboard data received:', {
          totalProjects: dashboardData.totalProjects,
          activeProjects: dashboardData.activeProjects,
          completedProjects: dashboardData.completedProjects,
        });

        // Ensure all required fields are present with defaults
        const processedData = {
          totalProjects: dashboardData.totalProjects || 0,
          activeProjects: dashboardData.activeProjects || 0,
          completedProjects: dashboardData.completedProjects || 0,
          totalBudget: dashboardData.totalBudget || 0,
          totalSpent: dashboardData.totalSpent || 0,
          averageProgress: dashboardData.averageProgress || 0,
          statusDistribution: dashboardData.statusDistribution || {
            planning: 0,
            active: 0,
            onHold: 0,
            completed: 0,
            cancelled: 0,
          },
          priorityDistribution: dashboardData.priorityDistribution || {
            low: 0,
            medium: 0,
            high: 0,
            critical: 0,
          },
          budgetData: dashboardData.budgetData || [],
          riskAnalysis: dashboardData.riskAnalysis || {
            totalRisks: 0,
            highRisks: 0,
            mediumRisks: 0,
            lowRisks: 0,
          },
          projectHealth: dashboardData.projectHealth || {
            healthy: 0,
            atRisk: 0,
            critical: 0,
          },
          recentActivities: dashboardData.recentActivities || [],
          upcomingMilestones: dashboardData.upcomingMilestones || [],
          timelineData: dashboardData.timelineData || [],
        };

        console.log(
          'Setting dashboard data with totalProjects:',
          processedData.totalProjects
        );
        setDashboardData(processedData);
      } else {
        const errorData = await dashboardResponse.json().catch(() => ({}));
        console.error('Error loading dashboard data - Response not OK:', {
          status: dashboardResponse.status,
          statusText: dashboardResponse.statusText,
          error: errorData,
        });
        showError(
          errorData.message ||
            `Failed to load dashboard data (${dashboardResponse.status}). Please check your connection.`
        );
        // Don't use mock data - show empty state instead
        setDashboardData({
          totalProjects: 0,
          activeProjects: 0,
          completedProjects: 0,
          totalBudget: 0,
          totalSpent: 0,
          averageProgress: 0,
          statusDistribution: {
            planning: 0,
            active: 0,
            onHold: 0,
            completed: 0,
            cancelled: 0,
          },
          priorityDistribution: {
            low: 0,
            medium: 0,
            high: 0,
            critical: 0,
          },
          budgetData: [],
          riskAnalysis: {
            totalRisks: 0,
            highRisks: 0,
            mediumRisks: 0,
            lowRisks: 0,
          },
          projectHealth: {
            healthy: 0,
            atRisk: 0,
            critical: 0,
          },
          recentActivities: [],
          upcomingMilestones: [],
          timelineData: [],
        });
      }
    } catch (err) {
      console.error('Error loading dashboard data - Exception:', err);
      showError(
        `Network error: ${err.message}. Please check your connection and try again.`
      );
      // Don't use mock data - show empty state instead
      setDashboardData({
        totalProjects: 0,
        activeProjects: 0,
        completedProjects: 0,
        totalBudget: 0,
        totalSpent: 0,
        averageProgress: 0,
        statusDistribution: {
          planning: 0,
          active: 0,
          onHold: 0,
          completed: 0,
          cancelled: 0,
        },
        priorityDistribution: {
          low: 0,
          medium: 0,
          high: 0,
          critical: 0,
        },
        budgetData: [],
        riskAnalysis: {
          totalRisks: 0,
          highRisks: 0,
          mediumRisks: 0,
          lowRisks: 0,
        },
        projectHealth: {
          healthy: 0,
          atRisk: 0,
          critical: 0,
        },
        recentActivities: [],
        upcomingMilestones: [],
        timelineData: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const processDashboardData = (projects) => {
    const totalProjects = projects.length;
    const activeProjects = projects.filter((p) => p.status === 'Active').length;
    const completedProjects = projects.filter(
      (p) => p.status === 'Completed'
    ).length;

    const totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
    const totalSpent = projects.reduce(
      (sum, p) => sum + (p.actualCost || 0),
      0
    );
    const averageProgress =
      projects.length > 0
        ? projects.reduce((sum, p) => sum + (p.progress || 0), 0) /
          projects.length
        : 0;

    // Status distribution
    const statusDistribution = {
      planning: projects.filter((p) => p.status === 'Planning').length,
      active: projects.filter((p) => p.status === 'Active').length,
      onHold: projects.filter((p) => p.status === 'On Hold').length,
      completed: projects.filter((p) => p.status === 'Completed').length,
      cancelled: projects.filter((p) => p.status === 'Cancelled').length,
    };

    // Priority distribution
    const priorityDistribution = {
      low: projects.filter((p) => p.priority === 'Low').length,
      medium: projects.filter((p) => p.priority === 'Medium').length,
      high: projects.filter((p) => p.priority === 'High').length,
      critical: projects.filter((p) => p.priority === 'Critical').length,
    };

    // Budget data for charts
    const budgetData = projects.map((p) => ({
      name: p.name,
      budget: p.budget || 0,
      actual: p.actualCost || 0,
      variance: (p.actualCost || 0) - (p.budget || 0),
    }));

    // Timeline data
    const timelineData = projects.map((p) => ({
      name: p.name,
      startDate: new Date(p.startDate),
      endDate: new Date(p.endDate),
      progress: p.progress || 0,
      status: p.status,
    }));

    // Recent activities (mock for now)
    const recentActivities = [
      {
        id: 1,
        type: 'project',
        title: 'New Project Created',
        description: 'Digital Transformation Initiative',
        time: '2 hours ago',
        status: 'success',
      },
      {
        id: 2,
        type: 'milestone',
        title: 'Milestone Completed',
        description: 'Phase 1 Development Complete',
        time: '4 hours ago',
        status: 'success',
      },
      {
        id: 3,
        type: 'risk',
        title: 'Risk Identified',
        description: 'High risk in Project Alpha',
        time: '6 hours ago',
        status: 'warning',
      },
    ];

    // Upcoming milestones
    const upcomingMilestones = [
      {
        id: 1,
        projectName: 'Digital Transformation',
        milestone: 'Phase 2 Review',
        dueDate: '2024-02-15',
        status: 'upcoming',
      },
      {
        id: 2,
        projectName: 'Mobile App Development',
        milestone: 'Beta Release',
        dueDate: '2024-02-20',
        status: 'upcoming',
      },
    ];

    // Risk analysis
    const totalRisks = projects.reduce(
      (sum, p) => sum + (p.risks?.length || 0),
      0
    );
    const riskAnalysis = {
      totalRisks,
      highRisks: Math.floor(totalRisks * 0.2),
      mediumRisks: Math.floor(totalRisks * 0.3),
      lowRisks: Math.floor(totalRisks * 0.5),
    };

    // Project health
    const healthyProjects = projects.filter(
      (p) => (p.progress || 0) > 80 && (p.actualCost || 0) <= (p.budget || 0)
    ).length;
    const atRiskProjects = projects.filter(
      (p) =>
        (p.progress || 0) < 50 || (p.actualCost || 0) > (p.budget || 0) * 1.1
    ).length;
    const criticalProjects = projects.filter(
      (p) =>
        (p.progress || 0) < 30 || (p.actualCost || 0) > (p.budget || 0) * 1.2
    ).length;

    return {
      totalProjects,
      activeProjects,
      completedProjects,
      totalBudget,
      totalSpent,
      averageProgress,
      statusDistribution,
      priorityDistribution,
      budgetData,
      timelineData,
      recentActivities,
      upcomingMilestones,
      riskAnalysis,
      projectHealth: {
        healthy: healthyProjects,
        atRisk: atRiskProjects,
        critical: criticalProjects,
      },
    };
  };

  const getMockDashboardData = () => ({
    totalProjects: 12,
    activeProjects: 8,
    completedProjects: 3,
    totalBudget: 2500000,
    totalSpent: 1250000,
    averageProgress: 65,
    statusDistribution: {
      planning: 2,
      active: 8,
      onHold: 1,
      completed: 3,
      cancelled: 0,
    },
    priorityDistribution: {
      low: 2,
      medium: 6,
      high: 3,
      critical: 1,
    },
    budgetData: [
      {
        name: 'Digital Transformation',
        budget: 500000,
        actual: 250000,
        variance: -250000,
      },
      { name: 'Mobile App', budget: 300000, actual: 180000, variance: -120000 },
      {
        name: 'Infrastructure',
        budget: 200000,
        actual: 220000,
        variance: 20000,
      },
    ],
    timelineData: [],
    recentActivities: [
      {
        id: 1,
        type: 'project',
        title: 'New Project Created',
        description: 'Digital Transformation Initiative',
        time: '2 hours ago',
        status: 'success',
      },
      {
        id: 2,
        type: 'milestone',
        title: 'Milestone Completed',
        description: 'Phase 1 Development Complete',
        time: '4 hours ago',
        status: 'success',
      },
    ],
    upcomingMilestones: [
      {
        id: 1,
        projectName: 'Digital Transformation',
        milestone: 'Phase 2 Review',
        dueDate: '2024-02-15',
        status: 'upcoming',
      },
    ],
    riskAnalysis: {
      totalRisks: 15,
      highRisks: 3,
      mediumRisks: 5,
      lowRisks: 7,
    },
    projectHealth: {
      healthy: 6,
      atRisk: 4,
      critical: 2,
    },
  });

  const handleRefresh = () => {
    loadDashboardData();
  };

  const handleCreateProject = () => {
    navigate('/projects/project-management/create');
  };

  const handleViewProjects = () => {
    navigate('/projects/project-management');
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/project-dashboard';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh dashboard data',
      disabled: loading,
    },
    {
      label: 'View All Projects',
      icon: <Eye size={18} />,
      onClick: handleViewProjects,
      variant: 'outline',
      tooltip: 'View all projects',
    },
    {
      label: 'New Project',
      icon: <Plus size={18} />,
      onClick: handleCreateProject,
      variant: 'primary',
      tooltip: 'Create a new project',
    },
  ];

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div className="page-title-section">
            <h1 className="page-title">Project Dashboard</h1>
            <p className="page-description">
              Comprehensive overview of all project activities and performance
              metrics
            </p>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Project Dashboard"
        description="Comprehensive overview of all project activities and performance metrics"
        actions={headerActions}
      />
      <div
        className="page-container"
        style={{ width: '100%', maxWidth: '100%' }}
      >
        {/* KPI Cards */}
        <div className="grid w-full max-w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
          {[
            {
              icon: BarChart3,
              value: dashboardData.totalProjects,
              label: 'Total Projects',
              trend: '+12%',
              trendIcon: TrendingUp,
              trendColor: 'text-[#059669]',
            },
            {
              icon: Activity,
              value: dashboardData.activeProjects,
              label: 'Active Projects',
              trend: '+8%',
              trendIcon: TrendingUp,
              trendColor: 'text-[#059669]',
            },
            {
              icon: CheckCircle,
              value: dashboardData.completedProjects,
              label: 'Completed',
              trend: '+15%',
              trendIcon: TrendingUp,
              trendColor: 'text-[#059669]',
            },
            {
              icon: DollarSign,
              value: `$${(dashboardData.totalBudget / 1000000).toFixed(1)}M`,
              label: 'Total Budget',
              trend: '+5%',
              trendIcon: TrendingUp,
              trendColor: 'text-[#059669]',
            },
            {
              icon: TrendingUp,
              value: `${dashboardData.averageProgress.toFixed(0)}%`,
              label: 'Avg Progress',
              trend: '+3%',
              trendIcon: TrendingUp,
              trendColor: 'text-[#059669]',
            },
            {
              icon: AlertTriangle,
              value: dashboardData.riskAnalysis.totalRisks,
              label: 'Total Risks',
              trend: '-2',
              trendIcon: TrendingDown,
              trendColor: 'text-[#dc2626]',
            },
          ].map(
            (
              { icon: Icon, value, label, trend, trendIcon: TIcon, trendColor },
              i
            ) => (
              <div
                key={i}
                className="relative border border-[var(--border-color)] bg-white"
              >
                {/* Accent line */}
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                {/* Content */}
                <div className="relative flex items-center gap-3 p-4">
                  {/* Trend – top right */}
                  <div
                    className={`absolute right-4 top-2 flex items-center gap-1 text-[12px] font-semibold ${trendColor}`}
                    style={{ fontFamily: 'var(--font-family)' }}
                  >
                    <TIcon size={14} />
                    {trend}
                  </div>

                  {/* Icon */}
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                    <Icon size={20} className="text-[#2D7DD2]" />
                  </div>

                  {/* Text */}
                  <div>
                    <div
                      className="text-[20px] font-bold text-[var(--text-primary)]"
                      style={{ fontFamily: 'var(--font-family)' }}
                    >
                      {value}
                    </div>
                    <div
                      className="mt-1 text-[13px] text-[var(--text-secondary)]"
                      style={{ fontFamily: 'var(--font-family)' }}
                    >
                      {label}
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>

        {/* Charts Section */}
        <div
          className="dashboard-charts-two-col mb-6 h-auto"
          style={{ width: '100%', maxWidth: '100%' }}
        >
          <div className="dashboard-left-col">
            {/* Project Status Distribution */}
            <div className="chart-card rounded-2xl">
              <div className="chart-header">
                <h3 className="chart-title">Project Status Distribution</h3>
                <div className="chart-actions">
                  <button className="btn-icon">
                    <Filter size={16} />
                  </button>
                  <button className="btn-icon">
                    <Download size={16} />
                  </button>
                </div>
              </div>
              <div className="status-distribution-grid">
                {[
                  { key: 'planning', label: 'Planning' },
                  { key: 'active', label: 'Active' },
                  { key: 'onHold', label: 'On Hold' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'cancelled', label: 'Cancelled' },
                ].map(({ key, label }) => (
                  <div key={key} className={`status-grid-item ${key}`}>
                    <div className="status-grid-label">{label}</div>
                    <div className="status-grid-count">
                      {dashboardData.statusDistribution[key]}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Health */}
            <div className="chart-card rounded-2xl">
              <div className="chart-header">
                <h3 className="chart-title">Project Health Overview</h3>
              </div>

              <div className="chart-content">
                <div className="health-metrics health-grid">
                  <div className="health-item healthy">
                    <div className="health-top">
                      <CheckCircle size={18} />
                      <span>Healthy</span>
                    </div>
                    <div className="health-value">
                      {dashboardData.projectHealth.healthy}
                    </div>
                  </div>

                  <div className="health-item at-risk">
                    <div className="health-top">
                      <AlertTriangle size={18} />
                      <span>At Risk</span>
                    </div>
                    <div className="health-value">
                      {dashboardData.projectHealth.atRisk}
                    </div>
                  </div>

                  <div className="health-item critical">
                    <div className="health-top">
                      <AlertTriangle size={18} />
                      <span>Critical</span>
                    </div>
                    <div className="health-value">
                      {dashboardData.projectHealth.critical}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Analysis */}
            <div className="chart-card rounded-2xl">
              <div className="chart-header">
                <h3 className="chart-title">Risk Analysis</h3>
              </div>

              <div className="chart-content">
                <div className="health-metrics health-grid">
                  <div className="health-item critical">
                    <div className="health-top">
                      <AlertTriangle size={18} />
                      <span>High Risk</span>
                    </div>
                    <div className="health-value">
                      {dashboardData.riskAnalysis.highRisks}
                    </div>
                  </div>

                  <div className="health-item at-risk">
                    <div className="health-top">
                      <AlertTriangle size={18} />
                      <span>Medium Risk</span>
                    </div>
                    <div className="health-value">
                      {dashboardData.riskAnalysis.mediumRisks}
                    </div>
                  </div>

                  <div className="health-item healthy">
                    <div className="health-top">
                      <AlertTriangle size={18} />
                      <span>Low Risk</span>
                    </div>
                    <div className="health-value">
                      {dashboardData.riskAnalysis.lowRisks}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-right-col">
            {/* Budget vs Actual */}
            <div className="chart-card rounded-2xl">
              <div className="chart-header">
                <h3 className="chart-title">Budget vs Actual Spending</h3>
                <div className="chart-actions">
                  <button
                    className="btn-text"
                    onClick={() => setShowBudgetModal(true)}
                  >
                    View All
                  </button>
                  <button className="btn-icon">
                    <Filter size={16} />
                  </button>
                  <button className="btn-icon">
                    <Download size={16} />
                  </button>
                </div>
              </div>

              <div className="budget-projects">
                {dashboardData.budgetData.length > 0 ? (
                  dashboardData.budgetData.map((item, index) => (
                    <div
                      key={index}
                      className={`budget-project ${
                        item.variance >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      <div className="project-name">{item.name}</div>

                      <div className="budget-row">
                        <span className="budget-label">Budget</span>
                        <span className="budget-value">
                          ${(item.budget / 1000).toFixed(0)}K
                        </span>
                      </div>

                      <div className="budget-row">
                        <span className="budget-label">Actual</span>
                        <span className="actual-value">
                          ${(item.actual / 1000).toFixed(0)}K
                        </span>
                      </div>

                      <div className="difference-row">
                        <span className="difference-label">Difference</span>
                        <span
                          className={`difference-value ${
                            item.variance >= 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {item.variance >= 0 ? '+' : ''}$
                          {(item.variance / 1000).toFixed(0)}K
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    No budget data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div
          className="dashboard-bottom-grid"
          style={{ width: '100%', maxWidth: '100%' }}
        >
          {/* Recent Activities */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Recent Activities</h3>
              <div className="chart-actions">
                <a href=" " className="view-all-link">
                  View All
                </a>
              </div>
            </div>
            <div className="activities-section">
              {dashboardData.recentActivities &&
              dashboardData.recentActivities.length > 0 ? (
                dashboardData.recentActivities.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">
                      {activity.type === 'project' && <BarChart3 size={20} />}
                      {activity.type === 'milestone' && (
                        <CheckCircle size={20} />
                      )}
                      {activity.type === 'risk' && <AlertTriangle size={20} />}
                      {activity.type === 'info' && <HelpCircle size={20} />}
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">{activity.title}</div>
                      <div className="activity-description">
                        {activity.description}
                      </div>
                      {activity.time && (
                        <div className="activity-time">{activity.time}</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-item">
                  <div className="activity-icon">
                    <HelpCircle size={20} />
                  </div>
                  <div className="activity-content">
                    <div className="activity-title">No recent activities</div>
                    <div className="activity-description">
                      Activities will appear here as projects are updated
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Milestones */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Upcoming Milestones</h3>
              <div className="chart-actions">
                <a href=" " className="view-all-link">
                  View All
                </a>
              </div>
            </div>
            <div className="milestones-section">
              {dashboardData.upcomingMilestones &&
              dashboardData.upcomingMilestones.length > 0 ? (
                dashboardData.upcomingMilestones.map((milestone) => (
                  <div key={milestone.id} className="milestone-item">
                    <div className="milestone-content">
                      <div className="milestone-title">
                        {milestone.projectName}
                      </div>
                      <div className="milestone-description">
                        {milestone.milestone}
                      </div>
                      {milestone.dueDate && (
                        <div className="milestone-date">
                          <Calendar size={14} />
                          {milestone.dueDate}
                        </div>
                      )}
                    </div>
                    {milestone.status && milestone.status !== 'info' && (
                      <div className={`milestone-status ${milestone.status}`}>
                        {milestone.status}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="milestone-item">
                  <div className="milestone-content">
                    <div className="milestone-title">
                      No upcoming milestones
                    </div>
                    <div className="milestone-description">
                      Milestones will appear here when they are scheduled
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectDashboard;
